/**
 * Upload a batch of redemption codes into a distribution's pool.
 *
 * Apple hands you codes as a spreadsheet. Paste the column, or drop the CSV — we parse
 * it in the BROWSER and post plain strings, so the file itself never leaves the machine.
 *
 * Re-uploading the same batch is safe: the server skips codes already in the pool and
 * reports how many were duplicates.
 */

import { useState } from 'react';
import { IconUpload, IconFileSpreadsheet, IconTicket } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  TextInput,
  Textarea,
  Alert,
  Badge,
  Paper,
  toast,
} from '@/components/ui';
import { Modal } from '@/components/ui/overlays';
import { useMutation } from '@tanstack/react-query';
import { useAppDistributionService } from '@actschurch/ccms-sdk';

const MAX_CODES = 5000;

interface UploadCodesModalProps {
  opened: boolean;
  onClose: () => void;
  distributionId: number;
  /** e.g. "CCMS Tasks — iOS", for the header. */
  targetLabel: string;
  onUploaded: () => void;
}

export default function UploadCodesModal({
  opened,
  onClose,
  distributionId,
  targetLabel,
  onUploaded,
}: UploadCodesModalProps) {
  const distributionService = useAppDistributionService();

  const [raw, setRaw] = useState('');
  const [label, setLabel] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const codes = parseCodes(raw);
  const tooMany = codes.length > MAX_CODES;

  const uploadMutation = useMutation({
    mutationFn: () =>
      distributionService.uploadCodeBatch(distributionId, {
        label: label.trim() || null,
        sourceNote: sourceNote.trim() || null,
        // Apple Business Manager codes do not expire — leave this empty for them.
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        codes,
      }),
    onSuccess: (result) => {
      const parts = [`${result.inserted} code(s) added`];
      if (result.duplicates > 0) parts.push(`${result.duplicates} already in the pool`);
      toast.success(parts.join(' · '));
      reset();
      onUploaded();
      onClose();
    },
    onError: (err: any) => toast.error('Upload failed', err?.message ?? ''),
  });

  const reset = () => {
    setRaw('');
    setLabel('');
    setSourceNote('');
    setExpiresAt('');
  };

  const handleFile = async (file: File) => {
    const text = await file.text();
    setRaw(text);
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={`Upload codes — ${targetLabel}`}
      size="lg"
    >
      <Stack gap="md">
        <Alert color="violet" icon={<IconTicket size={16} />}>
          <Text size="sm">
            Paste the codes below (one per line, or comma-separated), or drop the CSV Apple
            gave you. Codes already in the pool are skipped, so it is safe to re-upload the
            same file.
          </Text>
        </Alert>

        <Textarea
          label="Codes"
          placeholder={'XXXXXXXXXXXX\nYYYYYYYYYYYY\nZZZZZZZZZZZZ'}
          value={raw}
          onChange={(e) => setRaw(e.currentTarget.value)}
          minRows={8}
          autosize
          maxRows={16}
          styles={{ input: { fontFamily: 'monospace' } }}
        />

        <Group justify="space-between" align="center">
          <Button
            variant="light"
            size="xs"
            leftSection={<IconFileSpreadsheet size={16} />}
            component="label"
          >
            Choose a CSV
            <input
              type="file"
              accept=".csv,.txt,text/csv,text/plain"
              hidden
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                if (file) void handleFile(file);
                e.currentTarget.value = '';
              }}
            />
          </Button>

          {codes.length > 0 && (
            <Badge color={tooMany ? 'red' : 'violet'} variant="light" size="lg">
              {codes.length} unique code(s)
            </Badge>
          )}
        </Group>

        {tooMany && (
          <Alert color="red">
            <Text size="sm">
              That is more than {MAX_CODES.toLocaleString()} codes. Split it into smaller
              batches.
            </Text>
          </Alert>
        )}

        {codes.length > 0 && !tooMany && (
          <Paper withBorder p="xs" radius="sm">
            <Text size="xs" c="dimmed" mb={4}>
              Preview
            </Text>
            <Text size="xs" ff="monospace" c="dimmed">
              {codes.slice(0, 3).join(' · ')}
              {codes.length > 3 && ` · … and ${codes.length - 3} more`}
            </Text>
          </Paper>
        )}

        <Group grow>
          <TextInput
            label="Batch label"
            placeholder="Jul 2026 ABM top-up"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
          />
          <TextInput
            label="Expiry (optional)"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.currentTarget.value)}
            description="Leave blank for ABM codes — they don't expire."
          />
        </Group>

        <TextInput
          label="Where did these come from?"
          placeholder="Apple Business Manager order #1234"
          value={sourceNote}
          onChange={(e) => setSourceNote(e.currentTarget.value)}
        />

        <Group justify="flex-end">
          <Button
            variant="subtle"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            leftSection={<IconUpload size={16} />}
            onClick={() => uploadMutation.mutate()}
            loading={uploadMutation.isPending}
            disabled={codes.length === 0 || tooMany}
          >
            Upload {codes.length > 0 ? `${codes.length} code(s)` : 'codes'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * A CSV header cell, not a code. Apple's export ships with one, and pasting a column out
 * of a spreadsheet usually brings it along — without this, "Code" would be uploaded as a
 * code, handed to somebody, and fail to redeem.
 */
const HEADER_WORDS =
  /^(code|codes|redemption\s*code|redeem(ption)?|promo\s*code|voucher|value|content|number)$/i;

/**
 * Pull codes out of whatever the admin pasted. Handles newlines, commas, tabs and
 * semicolons — Apple's spreadsheet export, a copied column, and a hand-typed list all
 * arrive differently. Quotes are stripped, a header cell is dropped, and duplicates are
 * collapsed, so what we send is what we mean.
 */
function parseCodes(raw: string): string[] {
  if (!raw.trim()) return [];
  const seen = new Set<string>();

  const tokens = raw
    .split(/[\r\n,;\t]+/)
    .map((c) => c.trim().replace(/^["']|["']$/g, ''))
    .filter((c) => c.length > 0);

  // Only the FIRST token can be a header. A code that happens to read like one further
  // down the file is still a code.
  const body = tokens.length > 0 && HEADER_WORDS.test(tokens[0]) ? tokens.slice(1) : tokens;

  return body.filter((c) => {
    const key = c.toUpperCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
